import { ModalShell } from '../design/ModalShell';
import { WizardJourney } from '../design/WizardJourney';
import { Button } from '../design/Button';
import Kombi from '../../../design/Kombi';
import type { UploadOverlayVm } from '../useUploadOverlay';

/**
 * The completion message, VERBATIM from `Tool Upload P5 V2.svg` — the Design keeps it
 * in `fixtures/publish.ts`, and it is inlined here rather than dragging a fixtures
 * directory into the backend for two strings. It is REAL FRAME COPY, not a fixture
 * standing in for backend data: the frame draws these exact words, and the Design's
 * own note says "this is a static success message; there is nothing to wire".
 *
 * Two SEPARATE lines, not one wrapped string: the frame draws two tspans with their
 * own centre origins and a deliberate break, so a narrower viewport re-centres each
 * line but never re-wraps them together. "Kombi" is the VW Type 2 van — brand voice,
 * the design's own word. The apostrophe is the frame's typographic ’ (U+2019).
 */
const publishMessage: readonly string[] = [
  'Your Tool’s found its seat on the Kombi.',
  'Congratulations!',
];

/**
 * Tool Upload P5 V2 — "Publish your tool" (wizard step 4, the final screen)
 *
 * The last numbered wizard step (`activeStep=4`, pill 4 drawn @0.6). CHROME,
 * MODAL, STEP-PILL row and the two horizontal rules are identical to P2–P4 — the
 * same shared shell. What is different is that the BODY is nearly empty: a single
 * centred two-line congratulations message, and the footer carries ONE button
 * ("Finish") instead of P4's Cancel + Next.
 *
 * MEASURED from `Tool Upload P5 V2.svg`. Modal is the shared true box 190,231
 * 1056x640 r16 (collapsing 190.5,231.5 1055x639 r15.5). Coordinates are CANVAS.
 *
 * THE RULES — same as P4:
 *   header  191,328.5 -> 1245,328.5   full width   @1
 *   footer  190,788.5 -> 1245,788.5   FULL WIDTH   @1
 *   (no column rule — P5 has no two-column body, so nothing divides it.)
 *
 * THE MESSAGE, centred both ways (derived, crude framing first — see below):
 *   line 1  "Your Tool’s found its seat on the Kombi."  24/300  baseline 549.727  x=498.414
 *   line 2  "Congratulations!"                          24/300  baseline 585.727  x=625.879
 *   -> 36px baseline pitch == --leading-message. Both lines fill=black.
 *
 * VERTICAL PLACEMENT IS CENTRING, NOT A COORDINATE. Crude numbers first, per the
 * project's rule: between the header rule (328.5) and the footer rule (788.5) the
 * body band is 460 tall. The two-line block's cap-top is ~532.3 and its box-bottom
 * ~590.8, leaving ~203.8 above and ~197.7 below — a 6px asymmetry, and the block
 * centre (561.6) sits ~3px below the band centre (558.5). That 3-6px is within the
 * cap-top/descent approximation (the box-bottom is estimated from Inter's descent
 * ratio), so the honest reading is: the message is VERTICALLY CENTRED in the band
 * between the two rules. Centring is also the fluid, non-coordinate placement —
 * no invented top offset. HORIZONTAL: line origins 498.414 and 625.879 imply a
 * shared centre at the modal centre (718) with a consistent ~11px/char advance,
 * i.e. two different-length strings centred on the same axis. So: centre both ways.
 *
 * TWO LINES, not one wrapped string — the frame draws two tspans with their own
 * centres and a deliberate break. Rendered as two block elements (see fixture);
 * a narrower viewport re-centres each but never re-wraps them together.
 *
 * FOOTER — "Finish" only. No Cancel, no help line (P2/P3 had help; P4 had
 * Cancel+Next; P5 has just Finish). Same control + full-width rule as P4:
 *   Finish  1163.5,815.5  53x22  r7.5  stroke #022B30 @0.3
 *     -> right inset 29 (--modal-footer-r), rule-to-button gap via
 *        --wizard-footer-btn-gap, same as P4.
 *
 * WHAT CHANGED IN THE BACKEND COPY. Layout, classNames and every measurement above
 * are untouched. Only two things:
 *
 *   1. The `publishMessage` fixture import -> the same two lines inlined above. They
 *      are the FRAME'S OWN COPY, and the Design's own note says there is nothing to
 *      wire ("the frame gives one fixed message, so one fixed message is stored"), so
 *      nothing is wired: the backend's `UploadResult` carries ids and connector counts,
 *      not a success sentence, and substituting one would be invented copy.
 *   2. `navigate('/profile-full')` -> `vm.props.onUploaded(result)`, the backend's own
 *      completion callback (Share.tsx closes the overlay and refreshes its list). Same
 *      gesture — leave the wizard, having published — with no router.
 *
 * WHY THIS PAGE IS A PHASE, NOT A STAGE. P2/P3/P4 map to the `stage` union; this one is
 * reached by the UPLOAD SUCCEEDING (`vm.phase.kind === 'done'`), which is why the frame
 * renders it off the phase and why `activeStep={4}` below stays a literal — there is no
 * `stageIndex` for a screen that is not a stage.
 */
export function UploadPublishPage({ vm }: { vm: UploadOverlayVm }) {
  // Only ever rendered under `phase.kind === 'done'` (see UploadWizard's switch), which
  // is what carries the upload result. Read defensively rather than asserted: a null
  // result simply means Finish closes without re-reporting, never a crash.
  const result = vm.phase.kind === 'done' ? vm.phase.result : null;
  // Below the celebratory frame copy, a quiet line stating which route the tool actually took — read
  // from the REAL status the route echoes (the platform's moderation_mode decides it, not the
  // responsibility tick): 'Pending' ⇒ held for the AI SWAT team before it goes live (manual_review);
  // 'Approved' ⇒ published with no human step (self_moderated). Only shown once we have a result.
  const heldForReview = result?.publication_status === 'Pending';
  const publishedLive = result?.publication_status === 'Approved';

  return (
    <ModalShell
      label="Publish your tool"
      headerInset="pills"
      header={
        // Step pills (step 4 active), then the full-width header rule beneath
        // them — identical to P2–P4. -mx-step-row escapes the --step-row-inset
        // (65) the header region is padded by, so the rule spans 191..1245.
        <>
          <WizardJourney activeStep={4} />
          <div className="-mx-step-row mt-11 h-px bg-ink" />
        </>
      }
    >
      {/* The body: a full-height flex column so the centred message can occupy
          the band between the header and footer rules, with the footer pinned to
          the modal floor by flow (mt-auto). h-full makes the column fill the
          modal interior below the header. */}
      <div className="flex h-full min-w-0 flex-col">
        {/* The message. flex-1 takes the band between the rules; centre both
            axes. NOT a measured top offset — the derivation shows the block is
            centred in the band (see header). text-center handles the horizontal
            centre of each line on the shared axis; leading-message is the 36px
            baseline pitch the two lines are drawn with. 24/300 == text-xl
            font-light. Each fixture line is its own <p> so the deliberate
            two-line break holds and neither line re-wraps. */}
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <p className="text-xl font-light leading-message text-ink">
            {publishMessage.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </p>
          {result ? (
            <p className="mt-6 max-w-modal text-sm font-light text-ink/60">
              {heldForReview
                ? 'It is now with the AI SWAT team to check for confidentiality and security. Once they approve it, it is published as Approved.'
                : publishedLive
                  ? 'It is now published as Approved and visible in the catalogue.'
                  : 'It is saved as a draft, ready for you to publish.'}
            </p>
          ) : null}
        </div>

        {/* THE KOMBI ITSELF, arriving. The frame copy above has always said "Your Tool's found its
            seat on the Kombi" — the metaphor is the designer's, and this is the one screen in the
            product where showing it is not decoration bolted on but the sentence being illustrated.

            `arrive` is a ONE-SHOT: the bus drives in from the left and parks while the scenery keeps
            rolling. It plays once, when the upload has just succeeded, and never repeats — a looping
            celebration on a screen the builder sits on before clicking Finish would turn into a
            fidget. `band` is the short strip: transparent, no road or hills, bus on its own bottom
            edge, so it butts against the footer rule below without a sand band fighting the modal.

            DECORATIVE, deliberately: no `title`. The two lines of copy directly above say exactly
            what this picture says, so a labelled image would make a screen reader announce the
            metaphor twice.

            THE ONE MEASURED THING THIS CHANGES. P5's message is drawn vertically centred in the band
            between the header and footer rules (see the derivation in this file's header). Adding an
            illustration necessarily moves it: the message now centres in the space ABOVE the strip
            rather than in the whole band, so it sits higher by roughly half the strip's height. That
            is a real departure from `Tool Upload P5 V2.svg` and it is the intended trade — the frame
            reserved that emptiness for nothing in particular, and the alternative (overlaying the
            illustration behind live text) would hurt the copy's contrast for no gain. */}
        <Kombi variant="band" motion="arrive" />

        {/* FULL-WIDTH FOOTER — the same construction as P4: a full-width rule
            (canvas 190..1245, reached by -mx-modal-x escaping ModalShell's
            --modal-pad-x body padding), then a single right-aligned "Finish".
            -mr-modal-x pushes the button group to the modal's true right inset;
            pr-modal-footer-r is the measured 29 from that edge;
            pt-wizard-footer-btn is the rule-to-button-top gap. No gap-modal-footer
            needed with one button, but kept harmless for symmetry with P4. */}
        <div className="mt-auto pb-wizard-footer-b">
          <div className="-mx-modal-x h-px bg-ink" />
          <div className="-mr-modal-x flex items-start justify-end pr-modal-footer-r pt-wizard-footer-btn">
            {/* Dismisses the wizard — returns to the uploads list. The Design
                navigated to /profile-full; the backend reports the finished upload
                to its caller, which closes the overlay and refreshes the list. */}
            <Button
              variant="outline-square"
              size="xs"
              onClick={() => (result ? vm.props.onUploaded(result) : vm.props.onClose())}
            >
              Finish
            </Button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
